import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import catchOnly from 'utils/catchOnly';

import { selectInstantCredit } from 'state/instantCredit';
import { selectTenant } from 'state/tenant';
import { Status } from 'hooks/requests/fetchUtils';
import { NonClientError } from 'hooks/requests/fetchUtils';
import useAnalytics, { Event } from 'hooks/useAnalytics';
import { useMarkdownTranslate } from 'hooks/useTranslate/useTranslate';
import { useSnackbar } from 'notistack';

import { textToRecover } from 'fmc-sdk';

import useEmailDisclosure, {
  EmailDisclosureError,
  EmailType,
} from 'hooks/requests/emailDisclosure';

import useTextToRecover, {
  TextToRecoverError,
} from 'hooks/requests/textToRecover';

import DSPView from './DSPView';
import { useCachedAttribute } from 'features/ResetApplicationRoute';

import useFmcHost from 'hooks/useFmcHost';

type PropTypes = {
  checkoutApproval?: boolean;
};

const DigitalShoppingPass: React.FC<PropTypes> = props => {
  const { checkoutApproval } = props;
  const instantCredit = useSelector(selectInstantCredit);
  const tenant = useSelector(selectTenant);
  const [sendDisclosure, status] = useEmailDisclosure();
  const { enqueueSnackbar } = useSnackbar();
  const { applicantEmail } = useCachedAttribute();
  const { sendEvents } = useAnalytics();
  const [sendTextToRecover, textStatus] = useTextToRecover();
  const fmcDispatch = useFmcHost();
  const tMd = useMarkdownTranslate();

  const onTextToRecoverFinancingClick: () => void = useCallback(async () => {
    console.log('clicked text to recover financing');
  }, []);

  const onSendEmailClick: () => void = useCallback(async () => {
    try {
      // Our assumption is that, by this point, email has been provided.
      await sendDisclosure(applicantEmail as string, EmailType.NotBestApr);
      enqueueSnackbar(tMd('dsp.snackbar.notBestAprEmail.success'), {
        variant: 'success',
      });
    } catch (e) {
      if (catchOnly(e, NonClientError, EmailDisclosureError)) {
        enqueueSnackbar(tMd('dsp.snackbar.notBestAprEmail.error'), {
          variant: 'error',
          persist: true,
        });
      }
    }
  }, [sendDisclosure, applicantEmail, enqueueSnackbar, tMd]);

  const onTextToRecoverClick: () => void = useCallback(async () => {
    sendEvents([Event.TextToRecover]);
    fmcDispatch(textToRecover());
    try {
      await sendTextToRecover();
      enqueueSnackbar(tMd('dsp.snackbar.textToRecover.success'), {
        variant: 'success',
        persist: true,
      });
    } catch (e) {
      if (catchOnly(e, NonClientError, TextToRecoverError)) {
        enqueueSnackbar(tMd('dsp.snackbar.textToRecover.error'), {
          variant: 'error',
          persist: true,
        });
      }
    }
  }, [sendTextToRecover, enqueueSnackbar, sendEvents, fmcDispatch, tMd]);

  return (
    <DSPView
      checkoutApproval={checkoutApproval}
      instantCredit={instantCredit}
      tenant={tenant}
      onSendEmailClick={onSendEmailClick}
      sendingEmail={status === Status.IN_PROGRESS}
      sendingTextToRecover={textStatus === Status.IN_PROGRESS}
      onTextToRecoverClick={onTextToRecoverClick}
      onTextToRecoverFinancingClick={onTextToRecoverFinancingClick}
    />
  );
};

export default DigitalShoppingPass;
